import { useModeAwareQuery, useModeAwareMutation } from "./useModeAwareQuery";
import { useApi } from "./useApi";
import { useQueryClient } from "@tanstack/react-query";
import type { UserApi, CreateUserRequestApi, UpdateUserRequestApi, UserCompanyApi } from "@/types/api/users";
import type { User, UserRole, UserStatus, UserPermissions, CreateUserFormData } from "@/types/user";
import { mockUsers } from "@/lib/mock-users";

/**
 * Map API roleId to frontend UserRole
 */
function mapRoleIdToRole(roleId: number): UserRole {
  switch (roleId) {
    case 1:
      return 'administrator';
    case 2:
      return 'user';
    case 3:
      return 'user';
    default:
      return 'user';
  }
}

/**
 * Map frontend UserRole to API roleId
 */
function mapRoleToRoleId(role: UserRole): number {
  switch (role) {
    case 'administrator':
      return 1;
    case 'user':
      return 2;
    default:
      return 2;
  }
}

/**
 * Map API statusId to frontend UserStatus
 */
function mapStatusIdToStatus(statusId: number): UserStatus {
  switch (statusId) {
    case 1:
      return 'active';
    case 2:
      return 'inactive';
    default:
      return 'active';
  }
}

/**
 * Map frontend UserStatus to API statusId
 */
function mapStatusToStatusId(status: UserStatus): number {
  switch (status) {
    case 'active':
      return 1;
    case 'inactive':
      return 2;
    default:
      return 1;
  }
}

/**
 * Map API user to frontend User type
 */
function mapUserApiToUser(apiUser: UserApi): User {
  const permissions: UserPermissions = {
    manageUsers: apiUser.canManageUsers,
    manageCompanies: apiUser.canManageCompanies,
    manageIntegrations: apiUser.canManageIntegrations,
    configureAutomation: apiUser.canConfigureAutomation,
    configureAI: apiUser.canConfigureAI,
    viewReports: apiUser.canViewReports,
    manageAccounts: apiUser.canManageAccounts,
    sendCommunications: apiUser.canSendCommunications,
    logActivities: apiUser.canLogActivities,
    viewPayments: false,
    allocatePayments: false,
    reverseAllocations: false,
    customerAccountsView: 'none',
    customerAccountsEdit: 'none',
    customerAccountsReassign: false,
    collectionCasesView: 'none',
    collectionCasesCreate: false,
    collectionCasesEdit: 'none',
    collectionCasesDelete: false,
    collectionCasesReassign: false,
    promiseToPayView: 'none',
    promiseToPayCreate: false,
    promiseToPayEdit: 'none',
    promiseToPayDelete: false,
    promiseToPayReassign: false,
    viewCreditControlDashboard: false,
    viewCollectionsDashboard: false,
    viewDisputesDashboard: false,
    viewAgeingAnalysis: false,
    viewMultiCompanyDashboard: false,
    accessManagerViews: false,
    adminManageUsersView: false,
    adminManageUsersCreate: false,
    adminManageUsersEdit: false,
    adminManageUsersDelete: false,
    adminManageCompaniesView: false,
    adminManageCompaniesCreate: false,
    adminManageCompaniesEdit: false,
    adminManageCompaniesDelete: false,
    adminManageGeneralSettingsView: false,
    adminManageGeneralSettingsCreate: false,
    adminManageGeneralSettingsEdit: false,
    adminManageGeneralSettingsDelete: false,
    adminManageIntegrationsView: false,
    adminManageIntegrationsCreate: false,
    adminManageIntegrationsEdit: false,
    adminManageIntegrationsDelete: false,
    adminManageTemplatesView: false,
    adminManageTemplatesCreate: false,
    adminManageTemplatesEdit: false,
    adminManageTemplatesDelete: false,
    adminManageCollectionStrategiesView: false,
    adminManageCollectionStrategiesCreate: false,
    adminManageCollectionStrategiesEdit: false,
    adminManageCollectionStrategiesDelete: false,
    adminManageDisputeTypesView: false,
    adminManageDisputeTypesCreate: false,
    adminManageDisputeTypesEdit: false,
    adminManageDisputeTypesDelete: false,
    adminConfigureAIView: false,
    adminConfigureAICreate: false,
    adminConfigureAIEdit: false,
    adminConfigureAIDelete: false,
  };

    return {
      id: String(apiUser.id),
      evoUserId: apiUser.evoUserId ?? undefined,
      externalFirstName: apiUser.externalFirstName ?? undefined,
      externalLastName: apiUser.externalLastName ?? undefined,
      firstName: apiUser.firstName,
      lastName: apiUser.lastName,
    email: apiUser.email,
    phone: apiUser.phone ?? undefined,
    role: mapRoleIdToRole(apiUser.roleId),
    status: mapStatusIdToStatus(apiUser.statusId),
    avatar: apiUser.avatar ?? undefined,
    companies: [], // API doesn't return company associations in the list endpoint
    permissions,
    lastLogin: apiUser.lastLogin ?? undefined,
    createdAt: apiUser.createdAt,
    updatedAt: apiUser.updatedAt ?? undefined,
  };
}

/**
 * Map frontend CreateUserFormData to API CreateUserRequestApi
 */
function mapCreateUserFormToApi(formData: CreateUserFormData): CreateUserRequestApi {
  return {
    evoUserId: formData.evoUserId ?? null,
    externalFirstName: formData.externalFirstName ?? null,
    externalLastName: formData.externalLastName ?? null,
    firstName: formData.firstName,
    lastName: formData.lastName,
    email: formData.email,
    phone: formData.phone ?? null,
    roleId: mapRoleToRoleId(formData.role),
    statusId: mapStatusToStatusId(formData.status ?? 'active'),
    canManageUsers: formData.permissions.manageUsers,
    canManageCompanies: formData.permissions.manageCompanies,
    canManageIntegrations: formData.permissions.manageIntegrations,
    canConfigureAutomation: formData.permissions.configureAutomation,
    canConfigureAI: formData.permissions.configureAI,
    canViewReports: formData.permissions.viewReports,
    canManageAccounts: formData.permissions.manageAccounts,
    canSendCommunications: formData.permissions.sendCommunications,
    canLogActivities: formData.permissions.logActivities,
  };
}

/**
 * Map frontend User to API UpdateUserRequestApi
 */
function mapUserToUpdateApi(user: Partial<User>): UpdateUserRequestApi {
  return {
    firstName: user.firstName!,
    lastName: user.lastName!,
    email: user.email!,
    phone: user.phone ?? null,
    roleId: mapRoleToRoleId(user.role!),
    statusId: mapStatusToStatusId(user.status!),
    canManageUsers: user.permissions?.manageUsers ?? false,
    canManageCompanies: user.permissions?.manageCompanies ?? false,
    canManageIntegrations: user.permissions?.manageIntegrations ?? false,
    canConfigureAutomation: user.permissions?.configureAutomation ?? false,
    canConfigureAI: user.permissions?.configureAI ?? false,
    canViewReports: user.permissions?.viewReports ?? false,
    canManageAccounts: user.permissions?.manageAccounts ?? false,
    canSendCommunications: user.permissions?.sendCommunications ?? false,
    canLogActivities: user.permissions?.logActivities ?? false,
  };
}

/**
 * Hook to fetch users list data for the Users page
 * Respects demo/live mode - uses mock data in demo mode, API in live mode
 */
export function useUsersList() {
  const { apiGet } = useApi();

  return useModeAwareQuery<User[], Error>(
    ["settings-users"],
    async ({ signal }) => {
      const response = await apiGet<UserApi[]>("/api/v1/users", undefined, signal);
      return response.map(mapUserApiToUser);
    },
    mockUsers,
    {
      staleTime: 0,
      refetchOnMount: 'always',
    }
  );
}

/**
 * Hook to fetch users who have access to the current company.
 * Used for task assignment dropdowns to ensure tasks are only assigned
 * to users who can see them (same company scope as the task).
 */
export function useCompanyUsersList() {
  const { apiGet } = useApi();

  return useModeAwareQuery<User[], Error>(
    ["company-users"],
    async ({ signal }) => {
      const response = await apiGet<UserApi[]>("/api/v1/users/company", undefined, signal);
      return response.map(mapUserApiToUser);
    },
    mockUsers,
    {
      staleTime: 0,
      refetchOnMount: 'always',
    }
  );
}

/**
 * Hook to create a new user
 * Respects demo/live mode - uses mock data in demo mode, API in live mode
 */
export function useCreateUser() {
  const { apiPost } = useApi();
  const queryClient = useQueryClient();

  return useModeAwareMutation<User, Error, CreateUserFormData>(
    async (formData) => {
      const requestData = mapCreateUserFormToApi(formData);
      const response = await apiPost<UserApi>("/api/v1/users", requestData);
      return mapUserApiToUser(response);
    },
    (formData) => {
      // Demo mode: create a mock user
      const mockUser: User = {
        id: String(Date.now()),
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        role: formData.role,
        status: 'active',
        companies: formData.companies,
        permissions: formData.permissions,
        createdAt: new Date().toISOString(),
      };
      return mockUser;
    },
    {
      onSuccess: () => {
        // Invalidate the users list query to refetch
        queryClient.invalidateQueries({ queryKey: ["settings-users"] });
      },
    }
  );
}

/**
 * Hook to update a user
 * Respects demo/live mode - uses mock data in demo mode, API in live mode
 */
export function useUpdateUser() {
  const { apiPut } = useApi();
  const queryClient = useQueryClient();

  return useModeAwareMutation<User, Error, { userId: string; data: Partial<User> }>(
    async ({ userId, data }) => {
      const requestData = mapUserToUpdateApi(data);
      const response = await apiPut<UserApi>(`/api/v1/users/${userId}`, requestData);
      return mapUserApiToUser(response);
    },
    ({ data }) => {
      // Demo mode: return updated user
      return {
        ...data,
        updatedAt: new Date().toISOString(),
      } as User;
    },
    {
      onSuccess: () => {
        // Invalidate the users list query to refetch
        queryClient.invalidateQueries({ queryKey: ["settings-users"] });
      },
    }
  );
}

/**
 * Hook to delete a user
 * Respects demo/live mode - uses mock data in demo mode, API in live mode
 */
export function useDeleteUser() {
  const { apiDelete } = useApi();
  const queryClient = useQueryClient();

  return useModeAwareMutation<void, Error, string>(
    async (userId) => {
      await apiDelete(`/api/v1/users/${userId}`);
    },
    () => {
      // Demo mode: just return success
      return;
    },
    {
      onSuccess: () => {
        // Invalidate the users list query to refetch
        queryClient.invalidateQueries({ queryKey: ["settings-users"] });
      },
    }
  );
}

/**
 * User company type for frontend
 */
export interface UserCompany {
  id: number;
  name: string;
  code: string | null;
  isLinked: boolean;
}

/**
 * Hook to fetch user companies
 * Respects demo/live mode - uses mock data in demo mode, API in live mode
 */
export function useUserCompanies(userId: string | null) {
  const { apiGet } = useApi();

  // Mock data for demo mode - list of available companies with some pre-linked
  const mockUserCompanies: UserCompany[] = [
    { id: 1, name: 'Acme Corporation', code: 'ACME', isLinked: true },
    { id: 2, name: 'TechStart Inc.', code: 'TECH', isLinked: false },
    { id: 3, name: 'Global Solutions Ltd.', code: 'GLBL', isLinked: true },
    { id: 4, name: 'Innovation Partners', code: 'INNOV', isLinked: false },
    { id: 5, name: 'Enterprise Systems', code: 'ENTSYS', isLinked: false },
  ];

  return useModeAwareQuery<UserCompany[], Error>(
    ["user-companies", userId],
    async ({ signal }) => {
      if (!userId) return [];
      const response = await apiGet<UserCompanyApi[]>(`/api/v1/users/${userId}/companies`, undefined, signal);
      return response.map(c => ({
        id: c.id,
        name: c.name,
        code: c.code,
        isLinked: c.isLinked,
      }));
    },
    mockUserCompanies,
    {
      enabled: !!userId,
      staleTime: 0,
    }
  );
}

/**
 * Hook to update user companies
 * Respects demo/live mode - uses mock data in demo mode, API in live mode
 */
export function useUpdateUserCompanies() {
  const { apiPost } = useApi();
  const queryClient = useQueryClient();

  return useModeAwareMutation<UserCompany[], Error, { userId: string; companyIds: number[] }>(
    async ({ userId, companyIds }) => {
      const response = await apiPost<UserCompanyApi[]>(`/api/v1/users/${userId}/companies`, { companyIds });
      return response.map(c => ({
        id: c.id,
        name: c.name,
        code: c.code,
        isLinked: c.isLinked,
      }));
    },
    ({ companyIds }) => {
      // Demo mode: return mock updated companies
      return companyIds.map(id => ({
        id,
        name: `Company ${id}`,
        code: `C${id}`,
        isLinked: true,
      }));
    },
    {
      onSuccess: (_, { userId }) => {
        // Invalidate the user companies query to refetch
        queryClient.invalidateQueries({ queryKey: ["user-companies", userId] });
      },
    }
  );
}
